/**
 * Hero card used by payment-intent / inbound-payment detail pages.
 *
 * Large monospace amount + currency, status badge, and a meta line of
 * supplementary facts (tenant, direction, ids). The right-hand slot is
 * reserved for actions (refund, retry — coming later).
 */
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { formatMinorUnits } from "@/lib/format";
import { cn } from "@/lib/utils";

export function DetailHero({
  backHref,
  backLabel,
  id,
  amount,
  currency,
  stateLabel,
  stateVariant,
  meta,
  actions,
}: {
  backHref: string;
  backLabel: string;
  id: string;
  amount: string;
  currency: string;
  stateLabel: string;
  stateVariant: BadgeProps["variant"];
  meta: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="mb-6 space-y-3">
      <Button asChild variant="ghost" size="sm" className="-ml-2 h-auto px-2 text-muted-foreground">
        <Link to={backHref}>
          <ArrowLeft />
          {backLabel}
        </Link>
      </Button>

      <Card className={cn("p-5 sm:p-6")}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-2">
            <div className="flex items-baseline flex-wrap gap-3">
              <span className="font-mono text-3xl font-semibold tabular-nums sm:text-4xl">
                {formatMinorUnits(amount, currency)}
              </span>
              <Badge variant={stateVariant} className="capitalize">
                {stateLabel.replace(/_/g, " ")}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              {meta}
            </div>
            <code className="block truncate font-mono text-xs text-muted-foreground/80">
              {id}
            </code>
          </div>
          {actions ? <div className="flex shrink-0 gap-2">{actions}</div> : null}
        </div>
      </Card>
    </div>
  );
}
